import { useState, useEffect, useCallback } from 'react';
import { apiFetch, DPP_BASE_URL } from '../dppApi';

// The brand's maintenance queue (dashboard "Maintenance" tab). Lists incoming
// repair/service requests and lets the merchant move each through its lifecycle;
// completing one records the repair (shown on the passport).

const fulfilmentLabel = (f) => (f === 'pickup' ? 'Pick up at home' : 'Drop off at store');
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
const stateLabel = (s) => cap((s || '').replace('_', ' '));   // 'in_service' -> 'In service'

// The queue is grouped by lifecycle stage; each group collapses/expands with a
// chevron (same control as the inquiry dashboard). Terminal groups start collapsed.
const MAINTENANCE_GROUPS = [
    { key: 'new', title: 'New', statuses: ['pending'] },
    { key: 'inprogress', title: 'In progress', statuses: ['accepted', 'in_service', 'ready'] },
    { key: 'completed', title: 'Completed', statuses: ['completed'] },
    { key: 'cancelled', title: 'Cancelled', statuses: ['cancelled'] },
];

const BrandMaintenance = ({ token, storeId }) => {
    const [requests, setRequests] = useState(null);
    const [error, setError] = useState(null);
    const [complete, setComplete] = useState(null);   // { id, description, performed_by }
    const [walkIn, setWalkIn] = useState(null);        // { qr_code_id, issue, fulfilment } | null
    // Terminal groups start collapsed — attention is on active work.
    const [collapsed, setCollapsed] = useState({ completed: true, cancelled: true });
    const toggle = (key) => setCollapsed(c => ({ ...c, [key]: !c[key] }));

    const load = useCallback(async () => {
        const qs = storeId && storeId !== 'all' ? `?store_id=${storeId}` : '';
        try { setRequests(await apiFetch(`/api/brand/maintenance${qs}`, { token })); setError(null); }
        catch (err) { setError(err.message); }
    }, [token, storeId]);

    useEffect(() => { load(); }, [load]);

    const patch = async (id, body) => {
        try { await apiFetch(`/api/brand/maintenance/${id}`, { method: 'PATCH', token, body }); setComplete(null); load(); }
        catch (err) { setError(err.message); }
    };

    const logWalkIn = async () => {
        try {
            await apiFetch('/api/brand/maintenance', { method: 'POST', token, body: walkIn });
            setWalkIn(null); load();
        } catch (err) { setError(err.message); }
    };

    if (!requests && !error) return <p className="collection-status collection-status--flush">Loading…</p>;

    const walkInBar = walkIn ? (
        <div className="dpp-card maintenance-walkin-form">
            <p className="maintenance-walkin-title">Log a walk-in</p>
            <label className="dpp-transfer-label">Passport ID *</label>
            <input className="dpp-input" placeholder="Piece QR / passport ID"
                value={walkIn.qr_code_id} onChange={e => setWalkIn({ ...walkIn, qr_code_id: e.target.value })} />
            <label className="dpp-transfer-label">Issue</label>
            <input className="dpp-input" placeholder="What needs doing — e.g. Clasp repair"
                value={walkIn.issue} onChange={e => setWalkIn({ ...walkIn, issue: e.target.value })} />
            <label className="dpp-transfer-label">Fulfilment</label>
            <select className="dpp-input" value={walkIn.fulfilment} onChange={e => setWalkIn({ ...walkIn, fulfilment: e.target.value })}>
                <option value="dropoff">Drop off at store</option>
                <option value="pickup">Pick up at home</option>
            </select>
            <div className="maintenance-actions">
                <button className="catalogue-btn-primary" disabled={!walkIn.qr_code_id.trim()} onClick={logWalkIn}>Log request</button>
                <button className="catalogue-btn-secondary" onClick={() => setWalkIn(null)}>Cancel</button>
            </div>
        </div>
    ) : (
        <button className="catalogue-hub-action maintenance-walkin-add" onClick={() => setWalkIn({ qr_code_id: '', issue: '', fulfilment: 'dropoff' })}>
            Log walk-in
        </button>
    );

    const renderRequest = (r) => {
        const closed = r.status === 'completed' || r.status === 'cancelled';
        const editing = complete?.id === r.request_id;
        const initial = (r.product || '?').trim().charAt(0).toUpperCase();
        return (
                    <div className="dpp-card maintenance-card" key={r.request_id}>
                        <div className="maintenance-main">
                            <div className="maintenance-thumb">
                                {r.image
                                    ? <img src={r.image} alt={r.product || 'item'} />
                                    : <span className="maintenance-thumb-fallback">{initial}</span>}
                            </div>
                            <div className="maintenance-body">
                                <div className="maintenance-head">
                                    <span className="maintenance-piece">{r.product || '—'}</span>
                                    <span className={`maintenance-status maintenance-status--${r.status}`}>{stateLabel(r.status)}</span>
                                </div>
                                {r.issue && <p className="maintenance-issue">“{cap(r.issue)}”</p>}
                                <p className="maintenance-sub">
                                    {r.requester || 'Walk-in'} · {fulfilmentLabel(r.fulfilment)} · via{' '}
                                    {/* When the request came in via the passport, the word "Passport"
                                        IS the link — no redundant "via Passport · Passport". Other
                                        sources keep a separate trailing Passport link. */}
                                    {r.source === 'passport' && r.qr_code_id ? (
                                        <a className="passport-link" href={`${DPP_BASE_URL}/jewellery/${r.qr_code_id}?as=brand&page=3`} target="_blank" rel="noreferrer">Passport</a>
                                    ) : (
                                        <>
                                            {cap((r.source || '').replace('_', ' '))}
                                            {r.qr_code_id && <>
                                                {' · '}
                                                <a className="passport-link" href={`${DPP_BASE_URL}/jewellery/${r.qr_code_id}?as=brand&page=3`} target="_blank" rel="noreferrer">Passport</a>
                                            </>}
                                        </>
                                    )}
                                </p>
                            </div>
                        </div>

                        {!editing && (
                            <div className="maintenance-actions">
                                <select className="maintenance-select" value={r.status}
                                    onChange={e => { const v = e.target.value; if (v === 'completed') setComplete({ id: r.request_id, description: '', performed_by: '' }); else patch(r.request_id, { status: v }); }}>
                                    {['pending', 'accepted', 'in_service', 'ready', 'completed', 'cancelled'].map(s => <option key={s} value={s}>{stateLabel(s)}</option>)}
                                </select>
                                {!closed && <button className="catalogue-hub-action" onClick={() => setComplete({ id: r.request_id, description: '', performed_by: '' })}>Mark completed</button>}
                            </div>
                        )}

                        {editing && (
                            <div className="maintenance-complete">
                                <label className="dpp-transfer-label">What was done</label>
                                <input className="dpp-input" placeholder="e.g. Gem replaced"
                                    value={complete.description} onChange={e => setComplete({ ...complete, description: e.target.value })} />
                                <label className="dpp-transfer-label">Store / authorised merchant</label>
                                <input className="dpp-input" placeholder="Where the work was done"
                                    value={complete.performed_by} onChange={e => setComplete({ ...complete, performed_by: e.target.value })} />
                                <div className="maintenance-actions">
                                    <button className="catalogue-btn-primary" onClick={() => patch(r.request_id, { status: 'completed', description: complete.description, performed_by: complete.performed_by })}>Save &amp; complete</button>
                                    <button className="catalogue-btn-secondary" onClick={() => setComplete(null)}>Cancel</button>
                                </div>
                            </div>
                        )}

                    </div>
        );
    };

    return (
        <div className="maintenance-queue">
            {walkInBar}
            {error && <p className="dpp-error">{error}</p>}
            {requests && !requests.length && (
                <p className="collection-status collection-status--flush section-empty">No maintenance requests yet.</p>
            )}
            {MAINTENANCE_GROUPS.map(g => {
                const items = (requests || []).filter(r => g.statuses.includes(r.status));
                if (!items.length) return null;
                const isCollapsed = collapsed[g.key];
                return (
                    <section className="inquiry-group" key={g.key}>
                        <button type="button" className="inquiry-group-title inquiry-group-toggle" onClick={() => toggle(g.key)} aria-expanded={!isCollapsed}>
                            <span className={`inquiry-group-chevron${isCollapsed ? '' : ' is-open'}`} aria-hidden="true">
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
                            </span>
                            <span>{g.title}</span><span className="inquiry-group-count">{items.length}</span>
                        </button>
                        {!isCollapsed && items.map(renderRequest)}
                    </section>
                );
            })}
        </div>
    );
};

export default BrandMaintenance;
